"use client";

import React, { useEffect, useRef } from "react";
import { IChartApi, ISeriesApi, LineSeries, Time } from "lightweight-charts";
import type { WSDeltaTrendPayload } from "@lacon/ws";

export interface DeltaTrendProps {
  chartInstance: IChartApi | null;
  candleSeries: ISeriesApi<"Candlestick"> | null;
  isEnabled: boolean;
  deltaTrend: WSDeltaTrendPayload | null;
  formatTime: (timestamp: number) => number;
}

export const DeltaTrendIndicator: React.FC<DeltaTrendProps> = ({
  chartInstance,
  // candleSeries is required by BaseIndicatorProps but not used here
  isEnabled,
  deltaTrend,
  formatTime,
}) => {
  const lineRef = useRef<ISeriesApi<"Line"> | null>(null);

  // Create/remove the delta line series as enabled/disabled or chart changes
  useEffect(() => {
    if (!chartInstance) {
      return;
    }

    if (!isEnabled) {
      if (lineRef.current) {
        try {
          chartInstance.removeSeries(lineRef.current);
        } catch {}
        lineRef.current = null;
      }
      return;
    }

    if (!lineRef.current) {
      try {
        lineRef.current = chartInstance.addSeries(LineSeries, {
          color: "#60a5fa", // blue-400
          lineWidth: 2,
          lineStyle: 0,
          priceLineVisible: false,
        });
      } catch {}
    }

    return () => {
      if (lineRef.current) {
        try {
          chartInstance.removeSeries(lineRef.current);
        } catch {}
        lineRef.current = null;
      }
    };
  }, [chartInstance, isEnabled]);

  // Update line data and markers when payload changes
  useEffect(() => {
    if (!isEnabled || !lineRef.current) {
      try {
        lineRef.current?.setData([]);
      } catch {}
      return;
    }
    if (!deltaTrend) {
      try {
        lineRef.current.setData([]);
      } catch {}
      return;
    }

    // Set line points
    const linePoints = (deltaTrend.line || [])
      .filter(
        (p) =>
          typeof p.price === "number" &&
          isFinite(p.price) &&
          typeof p.timestamp === "number" &&
          isFinite(p.timestamp)
      )
      .map((p) => ({
        time: formatTime(p.timestamp) as Time,
        value: p.price,
      }))
      .sort((a, b) => (a.time as number) - (b.time as number));
    try {
      lineRef.current.setData(linePoints);
    } catch {}
  }, [deltaTrend, isEnabled, formatTime]);

  return null;
};
